import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useFiatDetailsMutation, useFiatApproveMutation, useFiatRejectMutation } from "../redux/features/user/userApi";
import Navbar from "../Components/Navbar";
import { Eye, Download, ArrowLeft, CheckCircle, XCircle, Calendar, Filter, FileText } from "lucide-react";
import { toast } from "react-toastify";

const FiatTransfers = () => {
  const navigate = useNavigate();
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectRemarks, setRejectRemarks] = useState("");
  const [rejectTransactionId, setRejectTransactionId] = useState("");
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveTransactionId, setApproveTransactionId] = useState("");
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedProofImage, setSelectedProofImage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [fetchDetails, { data, isLoading, isError, error }] = useFiatDetailsMutation();
  const [approveTransaction, { isLoading: isApproving }] = useFiatApproveMutation();
  const [rejectTransaction, { isLoading: isRejecting }] = useFiatRejectMutation();

  useEffect(() => {
    fetchDetails("deposit"); // Call the mutation with parameter
  }, [fetchDetails]);

  const transactions = useMemo(() => data?.data || [], [data]);

  const statuswrd = (type) => {
    switch (type) {
      case 1:
        return 'Approved';
      case 0:
        return 'Rejected';
      case 2:
        return 'Pending';
      default:
        return 'Not Defined';
    }
  }
  const filteredTransactions = useMemo(() => {
    return transactions.filter((transaction) => {
      // Search filter
      const searchMatch = !searchTerm ||
        transaction?.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction?.paymentId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction?.amount?.toString().includes(searchTerm) ||
        transaction?.currency?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction?.category?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction?.paymentMethod?.toLowerCase().includes(searchTerm.toLowerCase());

      // Date filter
      const transactionDate = new Date(transaction?.date);
      const start = startDate ? new Date(startDate) : null;
      const end = endDate ? new Date(endDate + "T23:59:59") : null;

      const dateMatch = (!start || transactionDate >= start) &&
        (!end || transactionDate <= end);

      // Status filter
      const statusMatch = statusFilter === "all" ||
        statuswrd(transaction?.status)?.toLowerCase() === statusFilter.toLowerCase();

      return searchMatch && dateMatch && statusMatch;
    });
  }, [transactions, searchTerm, startDate, endDate, statusFilter]);

  const paginatedTransactions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredTransactions.slice(startIndex, endIndex);
  }, [filteredTransactions, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredTransactions.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, startDate, endDate, statusFilter]);

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateString || "-";
    }
  };




  const getStatusColor = (status) => {
    switch (status) {
      case 1:
        return 'bg-green-100 text-green-800 border-green-200';
      case 0:
        return 'bg-red-100 text-red-800 border-red-200';
      case 2:
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const handleViewDetails = (transaction) => {
    setSelectedTransaction(transaction);
    setShowDetail(true);
  };

  const handleBack = () => {
    setShowDetail(false);
    setSelectedTransaction(null);
  };

  const handleApprove = async (transactionId) => {

    console.log(`transactionId----111-----,FiatTransfers------>`, transactionId);

    setApproveTransactionId(transactionId);
    setShowApproveModal(true);
  };

  const confirmApprove = async () => {
    try {
      const result = await approveTransaction(approveTransactionId).unwrap();
      setShowApproveModal(false);
      setApproveTransactionId("");
      fetchDetails("deposit"); // Refresh data
      toast.success(result.message || "Transaction approved successfully");
      // Navigate back after a short delay to allow toast to be seen
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error) {
      console.error("Approval failed:", error);
      toast.error(error?.data?.message || "Failed to approve transaction");
    }
  };

  const handleReject = (transactionId) => {
    setRejectTransactionId(transactionId);
    setShowRejectModal(true);
  };

  const confirmReject = async () => {
    try {
      const result = await rejectTransaction({
        transactionId: rejectTransactionId,
        remarks: rejectRemarks
      }).unwrap();
      setShowRejectModal(false);
      setRejectRemarks("");
      setRejectTransactionId("");
      fetchDetails("deposit"); // Refresh data
      toast.success(result.message || "Transaction rejected successfully");
      // Navigate back after a short delay to allow toast to be seen
      setTimeout(() => {
        navigate(-1);
      }, 1500);
    } catch (error) {
      console.error("Rejection failed:", error);
      toast.error(error?.data?.message || "Failed to reject transaction");
    }
  };

  const handleViewProof = (proofUrl) => {
    setSelectedProofImage(proofUrl);
    setShowImageModal(true);
  };

  const downloadProofImage = async (imageUrl, filename) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `proof_${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to download image:", error);
    }
  };

  const downloadReceipt = (transaction) => {
    const receiptData = {
      transactionId: transaction.paymentId,
      userId: transaction.userId,
      amount: transaction.amount,
      currency: transaction.currency,
      type: transaction.type,
      status: transaction.status,
      createdAt: transaction.createdAt,
      bankDetails: transaction.bankDetails,
      remarks: transaction.remarks
    };

    const dataStr = JSON.stringify(receiptData, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);

    const exportFileDefaultName = `receipt_${transaction.paymentId}_${Date.now()}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  const exportFilteredData = () => {
    const headers = ["Transaction ID", "User ID", "Amount", "Currency", "Type", "Status", "Created At"];
    const csvRows = [headers.join(",")];

    filteredTransactions.forEach(transaction => {
      const row = [
        transaction.paymentId || "",
        transaction.userId || "",
        transaction.amount || "",
        transaction.currency || "",
        transaction.paymentMethod || "",
        transaction.status || "",
        formatDate(transaction.date) || ""
      ].map(field => `"${field}"`);
      csvRows.push(row.join(","));
    });

    const csvContent = csvRows.join("\n");
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `fiat_transactions_${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (showDetail && selectedTransaction) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-gray-50 pt-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <button
                      onClick={handleBack}
                      className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                    >
                      <ArrowLeft className="w-5 h-5" />
                    </button>
                    <div>
                      <h1 className="text-2xl font-bold">Transaction Details</h1>
                      <p className="text-blue-100 mt-1">ID: {selectedTransaction.paymentId?.toUpperCase()}</p>
                    </div>
                  </div>
                  {/* <button
                    onClick={() => downloadReceipt(selectedTransaction)}
                    className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download Receipt
                  </button> */}
                </div>
              </div>

              {/* Content */}
              <div className="p-6 space-y-6">
                {/* Status Badge */}
                <div className="flex items-center justify-between">
                  <span className={`px-4 py-2 rounded-full text-sm font-medium border ${getStatusColor(selectedTransaction.status)}`}>
                    {statuswrd(selectedTransaction.status)}
                  </span>
                  <span className="text-sm text-gray-500">
                    {formatDate(selectedTransaction.date)}
                  </span>
                </div>

                {/* Transaction Info Grid */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-medium text-gray-500">User Email</label>
                      <p className="text-lg font-semibold text-gray-900">{selectedTransaction.email}</p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Amount</label>
                      <p className="text-2xl font-bold text-blue-600">
                        {selectedTransaction.amount} {selectedTransaction.currency}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Transaction Method</label>
                      <p className="text-lg font-semibold text-gray-900 capitalize">
                        {selectedTransaction.paymentMethod?.toUpperCase()}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Approved By</label>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-700">
                          {selectedTransaction.Action_by.split('@')[0] || '---'}
                        </p>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-500">Last updated At</label>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-700">
                          {formatDate(selectedTransaction.Action_at)}
                        </p>
                      </div>
                    </div>
                  
                  </div>

                  <div className="space-y-4">

                    {selectedTransaction.proof && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Proof</label>
                        <div className="bg-gray-50 p-3 rounded-lg">
                          <div className="flex items-center justify-between">
                            {/* <p className="text-sm text-gray-700 truncate flex-1">
                              {selectedTransaction.proof || 'Not provided'}
                            </p> */}
                            <div className="flex gap-2 ml-2">
                              <button
                                onClick={() => handleViewProof(selectedTransaction.proof)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                              >
                                View
                              </button>
                              <button
                                onClick={() => downloadProofImage(selectedTransaction.proof, `proof_${selectedTransaction.paymentId}.jpg`)}
                                className="text-green-600 hover:text-green-800 text-sm font-medium"
                              >
                                Download
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}



                    <div>
                      <label className="text-sm font-medium text-gray-500">Notes</label>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-700">
                          {selectedTransaction.comment || 'No notes'}
                        </p>
                      </div>
                    </div>

                    {
                      selectedTransaction.reason && (
                        <div>
                          <label className="text-sm font-medium text-gray-500">Reject reason</label>
                          <div className="bg-gray-50 p-3 rounded-lg">
                            <p className="text-sm text-red-500">
                              {selectedTransaction.reason || 'No reason'}
                            </p>
                          </div>
                        </div>
                      )
                    }
                      <div>
                      <label className="text-sm font-medium text-gray-500">Approved IP</label>
                      <div className="bg-gray-50 p-3 rounded-lg">
                        <p className="text-sm text-gray-700">
                          {(selectedTransaction.Action_ip)|| '----'}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                {
                  statuswrd(selectedTransaction.status)?.toLowerCase() === 'pending' && (
                    <div className="flex gap-4 pt-4 border-t">
                      <button
                        onClick={() => handleApprove(selectedTransaction.paymentId)}
                        className="flex-1 bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        <CheckCircle className="w-5 h-5" />
                        Approve Transaction
                      </button>
                      <button
                        onClick={() => handleReject(selectedTransaction.paymentId)}
                        className="flex-1 bg-red-600 hover:bg-red-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
                      >
                        <XCircle className="w-5 h-5" />
                        Reject Transaction
                      </button>
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>

        {/* Approve Confirmation Modal */}
        {showApproveModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Confirm Approval</h3>
              <p className="text-gray-600 mb-6">
                Are you sure you want to approve this transaction? This action cannot be undone.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowApproveModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmApprove}
                  disabled={isApproving}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  {isApproving ? 'Approving...' : 'Approve'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Reject Modal */}
        {showRejectModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl p-6 max-w-md w-full">
              <h3 className="text-lg font-semibold mb-4">Reject Transaction</h3>
              <p className="text-gray-600 mb-4">
                Please provide a reason for rejecting this transaction.
              </p>
              <textarea
                value={rejectRemarks}
                onChange={(e) => setRejectRemarks(e.target.value)}
                placeholder="Enter rejection reason..."
                className="w-full border border-gray-300 rounded-lg p-3 h-24 resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
              />
              <div className="flex gap-3 mt-4">
                <button
                  onClick={() => {
                    setShowRejectModal(false);
                    setRejectRemarks("");
                    setRejectTransactionId("");
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={confirmReject}
                  disabled={!rejectRemarks.trim() || isRejecting}
                  className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  {isRejecting ? 'Rejecting...' : 'Reject'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Image Viewer Modal */}
        {showImageModal && (
          <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-xl max-w-4xl max-h-[90vh] w-full overflow-hidden">
              <div className="flex items-center justify-between p-4 border-b">
                <h3 className="text-lg font-semibold">Proof Image</h3>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => downloadProofImage(selectedProofImage, `proof_${Date.now()}.jpg`)}
                    className="text-green-600 hover:text-green-800 px-3 py-1 text-sm font-medium"
                  >
                    Download
                  </button>
                  <button
                    onClick={() => setShowImageModal(false)}
                    className="text-gray-500 hover:text-gray-700 p-1"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className="p-4">
                <img
                  src={selectedProofImage}
                  alt="Proof"
                  className="max-w-full max-h-[70vh] object-contain mx-auto"
                />
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <>
      <Navbar />
      <div className="min-h-screen bg-gray-50 pt-20 px-4 sm:px-6 lg:px-8 pb-8">
        <div className=" mx-auto">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">FIAT Transactions</h1>
                <p className="text-gray-600 mt-1">Manage deposit and withdrawal requests</p>
              </div>
              <button
                onClick={exportFilteredData}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors"
              >
                <FileText className="w-4 h-4" />
                Export Filtered Data
              </button>
            </div>
          </div>

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div
              onClick={() => setStatusFilter("all")}
              className={`bg-white rounded-xl shadow-sm border p-4 cursor-pointer transition-all duration-200 ${statusFilter === "all"
                ? "border-blue-400 bg-blue-50 shadow-md transform scale-105"
                : "border-gray-200 hover:border-blue-300 hover:shadow-md hover:transform hover:scale-102"
                }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total</p>
                  <p className="text-2xl font-bold text-blue-900">{transactions.length}</p>
                </div>
                <div className="bg-blue-100 rounded-full p-3">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
              </div>
            </div>
            <div
              onClick={() => setStatusFilter("pending")}
              className={`bg-white rounded-xl shadow-sm border p-4 cursor-pointer transition-all duration-200 ${statusFilter === "pending"
                ? "border-yellow-400 bg-yellow-50 shadow-md transform scale-105"
                : "border-gray-200 hover:border-yellow-300 hover:shadow-md hover:transform hover:scale-102"
                }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Pending</p>
                  <p className="text-2xl font-bold text-yellow-600">{transactions.filter(t => t.status === 2).length}</p>
                </div>
                <div className="bg-yellow-100 rounded-full p-3">
                  <svg className="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
            <div
              onClick={() => setStatusFilter("approved")}
              className={`bg-white rounded-xl shadow-sm border p-4 cursor-pointer transition-all duration-200 ${statusFilter === "approved"
                ? "border-green-400 bg-green-50 shadow-md transform scale-105"
                : "border-gray-200 hover:border-green-300 hover:shadow-md hover:transform hover:scale-102"
                }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Approved</p>
                  <p className="text-2xl font-bold text-green-600">{transactions.filter(t => t.status === 1).length}</p>
                </div>
                <div className="bg-green-100 rounded-full p-3">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
            <div
              onClick={() => setStatusFilter("rejected")}
              className={`bg-white rounded-xl shadow-sm border p-4 cursor-pointer transition-all duration-200 ${statusFilter === "rejected"
                ? "border-red-400 bg-red-50 shadow-md transform scale-105"
                : "border-gray-200 hover:border-red-300 hover:shadow-md hover:transform hover:scale-102"
                }`}
            >
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Rejected</p>
                  <p className="text-2xl font-bold text-red-600">{transactions.filter(t => t.status === 0).length}</p>
                </div>
                <div className="bg-red-100 rounded-full p-3">
                  <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5 text-gray-500" />
              <h2 className="text-lg font-semibold text-gray-900">Filters</h2>
            </div>

            <div className="space-y-4">
              {/* Search and Date Filters */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Search
                  </label>
                  <input
                    type="text"
                    placeholder="Email, Payment ID, Amount, Currency..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Start Date
                  </label>
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    End Date
                  </label>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
                  />
                </div>
              </div>

              {/* Status Filter Buttons */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Status Filter
                </label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setStatusFilter("all")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${statusFilter === "all"
                      ? "bg-blue-600 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                  >
                    All
                  </button>
                  <button
                    onClick={() => setStatusFilter("pending")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${statusFilter === "pending"
                      ? "bg-yellow-600 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                  >
                    Pending
                  </button>
                  <button
                    onClick={() => setStatusFilter("approved")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${statusFilter === "approved"
                      ? "bg-green-600 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                  >
                    Approved
                  </button>
                  <button
                    onClick={() => setStatusFilter("rejected")}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition ${statusFilter === "rejected"
                      ? "bg-red-600 text-white"
                      : "bg-gray-200 text-gray-700 hover:bg-gray-300"
                      }`}
                  >
                    Rejected
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Transactions List */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            {isLoading ? (
              <div className="p-8 text-center">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <p className="mt-2 text-gray-600">Loading transactions...</p>
              </div>
            ) : isError ? (
              <div className="p-8 text-center">
                <div className="text-red-600">
                  {error?.data?.message || "Failed to load transactions"}
                </div>
              </div>
            ) : filteredTransactions.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                No transactions found matching your criteria.
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Transaction ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Email ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {paginatedTransactions.map((transaction) => (
                        <tr key={transaction.paymentId} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                            {transaction.paymentId?.toUpperCase()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {transaction.email}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                            {transaction.amount} {transaction.currency}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {transaction.category?.toUpperCase()}({transaction.paymentMethod?.toUpperCase()})
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(transaction.status)}`}>
                              {statuswrd(transaction.status)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {formatDate(transaction.date)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleViewDetails(transaction)}
                                className="text-blue-600 hover:text-blue-900 p-1 hover:bg-blue-50 rounded transition-colors"
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </button>

                              {transaction.status === 'pending' && (
                                <>
                                  <button
                                    onClick={() => handleApprove(transaction.paymentId)}
                                    className="text-green-600 hover:text-green-900 p-1 hover:bg-green-50 rounded transition-colors"
                                    title="Approve"
                                  >
                                    <CheckCircle className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => handleReject(transaction.paymentId)}
                                    className="text-red-600 hover:text-red-900 p-1 hover:bg-red-50 rounded transition-colors"
                                    title="Reject"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600">
                        Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredTransactions.length)} of {filteredTransactions.length} entries
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <select
                        value={itemsPerPage}
                        onChange={(e) => {
                          setItemsPerPage(Number(e.target.value));
                          setCurrentPage(1);
                        }}
                        className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value={10}>10</option>
                        <option value={25}>25</option>
                        <option value={50}>50</option>
                        <option value={100}>100</option>
                      </select>
                      <button
                        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Previous
                      </button>
                      <span className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1.5 border border-gray-300 rounded text-sm hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Approve Confirmation Modal */}
      {showApproveModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Confirm Approval</h3>
            <p className="text-gray-600 mb-6">
              Are you sure you want to approve this transaction? This action cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowApproveModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmApprove}
                disabled={isApproving}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {isApproving ? 'Approving...' : 'Approve'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4">Reject Transaction</h3>
            <p className="text-gray-600 mb-4">
              Please provide a reason for rejecting this transaction.
            </p>
            <textarea
              value={rejectRemarks}
              onChange={(e) => setRejectRemarks(e.target.value)}
              placeholder="Enter rejection reason..."
              className="w-full border border-gray-300 rounded-lg p-3 h-24 resize-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"
            />
            <div className="flex gap-3 mt-4">
              <button
                onClick={() => {
                  setShowRejectModal(false);
                  setRejectRemarks("");
                  setRejectTransactionId("");
                }}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmReject}
                disabled={!rejectRemarks.trim() || isRejecting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {isRejecting ? 'Rejecting...' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Image Viewer Modal */}
      {showImageModal && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl max-w-4xl max-h-[90vh] w-full overflow-hidden">
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold">Proof Image</h3>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => downloadProofImage(selectedProofImage, `proof_${Date.now()}.jpg`)}
                  className="text-green-600 hover:text-green-800 px-3 py-1 text-sm font-medium"
                >
                  Download
                </button>
                <button
                  onClick={() => setShowImageModal(false)}
                  className="text-gray-500 hover:text-gray-700 p-1"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="p-4">
              <img
                src={selectedProofImage}
                alt="Proof"
                className="max-w-full max-h-[70vh] object-contain mx-auto"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FiatTransfers;
